import { forwardRef } from 'react';

import { CardBody, CardFooter, HStack, Text, usePrefersReducedMotion, rem } from '@vimeo/bokeh';
import { humanize, translate } from '@vimeo/i18n-runtime';

import { Thumbnail } from '../Thumbnail';
import { VideoCardProps } from './types';
import { UserProfile, UserAvatar } from './UserProfile';
import { VideoCardBadge } from './VideoCardBadge';
import { VideoCardLayout } from './VideoCardLayout';

export const VideoCard = forwardRef<HTMLDivElement, VideoCardProps>(
  (
    {
      name,
      user,
      views,
      cardDetailsStylingProps,
      createdTime,
      description,
      isReducedMotion,
      customUserProfile,
      showUserAvatar = true,
      showUserName = true,
      children,
      progress,
      ...rest
    },
    ref,
  ) => {
    const isReducedMotionEnabled = usePrefersReducedMotion() || isReducedMotion;

    const humanizedViews = translate({
      singular: '{COUNT} view',
      plural: '{COUNT} views',
      count: views ?? 0,
      replacements: {
        COUNT: humanize(views ?? 0),
      },
    });

    const showFooterString = typeof views !== 'undefined' || typeof createdTime !== 'undefined';
    const footerString = [typeof views !== 'undefined' ? humanizedViews : null, createdTime]
      .filter(Boolean)
      .join(' • ');

    let ThumbnailComp: React.ReactNode;
    let restStylingProps;

    // Accepted thumbnail prop
    if ('thumbnail' in rest) {
      const { thumbnail, ...stylingProps } = rest;

      restStylingProps = stylingProps;
      ThumbnailComp = thumbnail;
    }

    if (!('thumbnail' in rest)) {
      const {
        alt,
        src,
        srcSet,
        loading,
        centerDecoration,
        topLeftDecoration,
        topRightDecoration,
        bottomLeftDecoration,
        bottomRightDecoration,
        duration,
        ...stylingProps
      } = rest;

      restStylingProps = stylingProps;
      ThumbnailComp = (
        <Thumbnail
          alt={alt}
          src={src}
          srcSet={srcSet}
          loading={loading}
          progress={progress}
          centerDecoration={centerDecoration}
          topLeftDecoration={topLeftDecoration}
          topRightDecoration={topRightDecoration}
          bottomLeftDecoration={bottomLeftDecoration}
          bottomRightDecoration={
            bottomRightDecoration || (
              <VideoCardBadge trackStatus={rest.trackStatus} duration={duration} />
            )
          }
        />
      );
    }

    const isHorizontalCard = restStylingProps?.direction === 'row';

    return (
      <VideoCardLayout
        data-group
        isReducedMotion={isReducedMotionEnabled}
        {...restStylingProps}
        ref={ref}
      >
        {ThumbnailComp}

        <HStack align="flex-start" flex="1" {...cardDetailsStylingProps}>
          {/* Render custom video info */}
          {customUserProfile ? (
            <CardBody display="flex" flexDirection="column" gap="50" flex="1">
              {customUserProfile}
            </CardBody>
          ) : null}

          {/* Render default video info */}
          {!customUserProfile ? (
            <>
              {user && showUserAvatar && !isHorizontalCard ? <UserAvatar user={user} /> : null}

              <CardBody
                display="flex"
                flexDirection="column"
                justifyContent="center"
                gap={rem(4)}
                flex="1"
                h="100%"
              >
                {name && (
                  <Text
                    title={name}
                    variant="heading-sm"
                    color="text-primary"
                    noOfLines={1}
                    wordBreak="break-all"
                  >
                    {name}
                  </Text>
                )}

                {/* Horizontal Card has full user profile in body */}
                {user && isHorizontalCard ? <UserProfile user={user} /> : null}

                {/* Vertical Card only has user name in body, user avatar is in separate leading above */}
                {user && showUserName && !isHorizontalCard ? (
                  <Text variant="body-sm" color="text-primary" noOfLines={1}>
                    {user.name}
                  </Text>
                ) : null}

                {description ? (
                  <Text variant="body-sm" color="text-secondary" noOfLines={3} mt="1">
                    {description}
                  </Text>
                ) : null}

                {showFooterString ? (
                  <Text variant="body-sm" color="text-secondary" noOfLines={1}>
                    {footerString}
                  </Text>
                ) : null}
              </CardBody>
            </>
          ) : null}

          {children ? <CardFooter>{children}</CardFooter> : null}
        </HStack>
      </VideoCardLayout>
    );
  },
);
